import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import * as api from '../services/api';
import './OrderForm.css';

function getFavorites(key) {
  try {
    return JSON.parse(localStorage.getItem(`fav_${key}`) || '[]');
  } catch { return []; }
}

function setFavorites(key, ids) {
  localStorage.setItem(`fav_${key}`, JSON.stringify(ids));
}

function AddressSearch({ addresses, value, onChange, placeholder, favoriteKey }) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [favIds, setFavIds] = useState(() => getFavorites(favoriteKey));
  const ref = useRef(null);

  const selected = addresses.find(a => a.id === value);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleFav = (e, addrId) => {
    e.stopPropagation();
    const next = favIds.includes(addrId)
      ? favIds.filter(id => id !== addrId)
      : [...favIds, addrId];
    setFavIds(next);
    setFavorites(favoriteKey, next);
  };

  const filtered = addresses.filter(addr => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      addr.name?.toLowerCase().includes(s) ||
      addr.address?.toLowerCase().includes(s) ||
      addr.postal_code?.toLowerCase().includes(s) ||
      addr.city?.toLowerCase().includes(s)
    );
  });

  const sorted = [...filtered].sort((a, b) => {
    const aFav = favIds.includes(a.id) ? 0 : 1;
    const bFav = favIds.includes(b.id) ? 0 : 1;
    return aFav - bFav;
  });

  return (
    <div className="address-search" ref={ref}>
      <div
        className={`address-search-input ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(true)}
      >
        {selected ? (
          <span className="address-selected">
            {favIds.includes(selected.id) && <span className="fav-star-inline">★ </span>}
            <strong>{selected.name}</strong> - {selected.address}, {selected.postal_code} {selected.city}
          </span>
        ) : (
          <span className="address-placeholder">{placeholder}</span>
        )}
      </div>

      {isOpen && (
        <div className="address-dropdown">
          <input
            type="text"
            className="address-filter-input"
            placeholder="Søg adresse..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <div className="address-options">
            {sorted.length === 0 ? (
              <div className="address-option empty">Ingen adresser fundet</div>
            ) : (
              sorted.map((addr, idx) => {
                const isFav = favIds.includes(addr.id);
                const prevIsFav = idx > 0 && favIds.includes(sorted[idx - 1].id);
                return (
                  <React.Fragment key={addr.id}>
                    {idx > 0 && prevIsFav && !isFav && (
                      <div className="address-fav-divider" />
                    )}
                    <div
                      className={`address-option ${addr.id === value ? 'selected' : ''} ${isFav ? 'is-fav' : ''}`}
                      onClick={() => {
                        onChange(addr.id);
                        setIsOpen(false);
                        setSearch('');
                      }}
                    >
                      <div className="address-option-content">
                        <div>
                          <strong>{addr.name}</strong>
                          <span>{addr.address}, {addr.postal_code} {addr.city}</span>
                        </div>
                        <button
                          className={`fav-star ${isFav ? 'active' : ''}`}
                          onClick={(e) => toggleFav(e, addr.id)}
                          title={isFav ? 'Fjern favorit' : 'Gør til favorit'}
                        >
                          {isFav ? '★' : '☆'}
                        </button>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const emptyLine = () => ({
  goods_description: '',
  quantity: 1,
  unit: '',
  po_number: '',
  pallet_eu: '',
  pallet_half: '',
  pallet_quarter: '',
  return_pallet_eu: '',
  return_pallet_half: '',
  return_pallet_quarter: '',
  temperature_requirement: '',
  time_delivery: false,
});

function OrderForm() {
  const [addresses, setAddresses] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tempRequirements, setTempRequirements] = useState([]);
  const [units, setUnits] = useState([]);
  const [surchargeTypes, setSurchargeTypes] = useState([]);
  const [pickupAddressId, setPickupAddressId] = useState('');
  const [deliveryAddressId, setDeliveryAddressId] = useState('');
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTime, setPickupTime] = useState('08:00');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('');
  const [pickupNotes, setPickupNotes] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [bookingReference, setBookingReference] = useState('');
  const [supplierRef, setSupplierRef] = useState('');
  const [lines, setLines] = useState([emptyLine()]);
  const [loading, setLoading] = useState(false);
  const [addressesLoading, setAddressesLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [editBookingId, setEditBookingId] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    fetchAddresses();
    fetchTemplates();
    fetchTemperatureRequirements();
    fetchUnits();
    fetchSurchargeTypes();
    const editId = searchParams.get('edit');
    const copyId = searchParams.get('copy');
    if (editId) {
      setEditBookingId(editId);
      setEditLoading(true);
      loadBookingForEdit(editId);
    } else if (copyId) {
      setEditLoading(true);
      loadBookingForEdit(copyId, true);
    }
    const today = new Date().toISOString().split('T')[0];
    setPickupDate(today);
  }, []);

  // Fetch AI suggestions when addresses change
  useEffect(() => {
    if (pickupAddressId || deliveryAddressId) {
      api.getAiSuggestions(pickupAddressId, deliveryAddressId)
        .then(setSuggestions)
        .catch(() => {});
    }
  }, [pickupAddressId, deliveryAddressId]);

  // Set default unit on lines when units load
  useEffect(() => {
    if (units.length > 0 && lines.length === 1 && !lines[0].unit) {
      setLines([{ ...lines[0], unit: units[0].name }]);
    }
  }, [units]);

  const fetchAddresses = async () => {
    try {
      const data = await api.getAddresses();
      setAddresses(data);
    } catch (error) {
      console.error('Error fetching addresses:', error);
    } finally {
      setAddressesLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const data = await api.getTemplates();
      setTemplates(data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchUnits = async () => {
    try {
      const data = await api.getUnits();
      setUnits(data);
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  };

  const fetchTemperatureRequirements = async () => {
    try {
      const data = await api.getTemperatureRequirements();
      setTempRequirements(data);
    } catch (error) {
      console.error('Error fetching temperature requirements:', error);
    }
  };

  const fetchSurchargeTypes = async () => {
    try {
      const data = await api.getSurchargeTypes();
      setSurchargeTypes(data);
    } catch (error) {
      console.error('Error fetching surcharge types:', error);
    }
  };

  const loadBookingForEdit = async (bookingId, isCopy = false) => {
    try {
      const booking = await api.getBooking(bookingId);
      if (!isCopy && booking.locked) {
        setError(t('lockedError'));
        setEditLoading(false);
        return;
      }
      // Match addresses by name
      const allAddresses = await api.getAddresses();
      const pickupAddr = allAddresses.find(a => a.name === booking.pickup_name);
      const deliveryAddr = allAddresses.find(a => a.name === booking.delivery_name);
      if (pickupAddr) setPickupAddressId(pickupAddr.id);
      if (deliveryAddr) setDeliveryAddressId(deliveryAddr.id);

      if (isCopy) {
        setPickupDate(new Date().toISOString().split('T')[0]);
      } else if (booking.pickup_date) {
        setPickupDate(booking.pickup_date.split('T')[0]);
      }
      if (booking.pickup_time) setPickupTime(booking.pickup_time);
      if (booking.delivery_date) setDeliveryDate(booking.delivery_date.split('T')[0]);
      if (booking.delivery_time) setDeliveryTime(booking.delivery_time);
      if (booking.pickup_notes) setPickupNotes(booking.pickup_notes);
      if (booking.delivery_notes) setDeliveryNotes(booking.delivery_notes);
      if (booking.booking_reference) setBookingReference(booking.booking_reference);

      if (booking.lines && booking.lines.length > 0) {
        setLines(booking.lines.map(l => ({
          goods_description: l.goods_description || '',
          quantity: l.quantity || 1,
          unit: l.unit || 'Læs',
          po_number: l.po_number || '',
          pallet_eu: l.pallet_eu || '',
          pallet_half: l.pallet_half || '',
          pallet_quarter: l.pallet_quarter || '',
          return_pallet_eu: l.return_pallet_eu || '',
          return_pallet_half: l.return_pallet_half || '',
          return_pallet_quarter: l.return_pallet_quarter || '',
          temperature_requirement: l.temperature_requirement || '',
          time_delivery: l.time_delivery || false,
        })));
      }
    } catch (err) {
      setError('Kunne ikke hente booking: ' + err.message);
    } finally {
      setEditLoading(false);
    }
  };

  const applyTemplate = (template) => {
    if (template.pickup_address_id) setPickupAddressId(template.pickup_address_id);
    if (template.delivery_address_id) setDeliveryAddressId(template.delivery_address_id);
    if (template.pickup_time) setPickupTime(template.pickup_time);
    if (template.delivery_time) setDeliveryTime(template.delivery_time);
    if (template.pickup_notes) setPickupNotes(template.pickup_notes);
    if (template.delivery_notes) setDeliveryNotes(template.delivery_notes);
    if (template.booking_reference) setBookingReference(template.booking_reference);
    if (template.quantity || template.unit || template.temperature_requirement) {
      setLines([{
        ...emptyLine(),
        quantity: template.quantity || 1,
        unit: template.unit || (units.length > 0 ? units[0].name : 'Læs'),
        temperature_requirement: template.temperature_requirement || '',
      }]);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) return;
    setSavingTemplate(true);
    try {
      const firstLine = lines[0] || {};
      const created = await api.createTemplate({
        name: templateName.trim(),
        pickup_address_id: pickupAddressId || null,
        delivery_address_id: deliveryAddressId || null,
        pickup_time: pickupTime,
        delivery_time: deliveryTime,
        pickup_notes: pickupNotes || null,
        delivery_notes: deliveryNotes || null,
        booking_reference: bookingReference || null,
        quantity: firstLine.quantity,
        unit: firstLine.unit,
        temperature_requirement: firstLine.temperature_requirement || null,
      });
      setTemplates([...templates, created]);
      setTemplateName('');
      setShowSaveTemplate(false);
    } catch (err) {
      console.error('Error saving template:', err);
    } finally {
      setSavingTemplate(false);
    }
  };

  const updateLine = (idx, field, value) => {
    setLines(prev => prev.map((line, i) => i === idx ? { ...line, [field]: value } : line));
  };

  const addLine = () => {
    const defaultUnit = units.length > 0 ? units[0].name : 'Læs';
    setLines(prev => [...prev, { ...emptyLine(), unit: defaultUnit }]);
  };

  const removeLine = (idx) => {
    if (lines.length <= 1) return;
    setLines(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!pickupAddressId || !deliveryAddressId) {
      setError('Vælg venligst både afsender og modtager adresse');
      setLoading(false);
      return;
    }

    try {
      const payload = {
        pickup_address_id: pickupAddressId,
        delivery_address_id: deliveryAddressId,
        pickup_date: pickupDate,
        pickup_time: pickupTime,
        delivery_date: deliveryDate || null,
        delivery_time: deliveryTime || null,
        pickup_notes: pickupNotes || null,
        delivery_notes: deliveryNotes || null,
        booking_reference: bookingReference || null,
        supplier_reference: supplierRef || null,
        lines: lines.map(l => ({
          goods_description: l.goods_description || null,
          quantity: l.quantity,
          unit: l.unit,
          po_number: l.po_number || null,
          pallet_eu: l.pallet_eu ? parseInt(l.pallet_eu) : null,
          pallet_half: l.pallet_half ? parseInt(l.pallet_half) : null,
          pallet_quarter: l.pallet_quarter ? parseInt(l.pallet_quarter) : null,
          return_pallet_eu: l.return_pallet_eu ? parseInt(l.return_pallet_eu) : null,
          return_pallet_half: l.return_pallet_half ? parseInt(l.return_pallet_half) : null,
          return_pallet_quarter: l.return_pallet_quarter ? parseInt(l.return_pallet_quarter) : null,
          temperature_requirement: l.temperature_requirement || null,
          time_delivery: l.time_delivery || false,
        })),
      };

      let data;
      if (editBookingId) {
        data = await api.updateBooking(editBookingId, payload);
        setSuccess(`Booking opdateret og sendt til godkendelse!`);
      } else {
        data = await api.createBooking(payload);
        setSuccess(`Ordre oprettet! Reference: ${data.booking_reference || '#' + data.booking_id}`);
      }

      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const timeOptions = [];
  for (let h = 6; h <= 20; h++) {
    for (let m = 0; m < 60; m += 30) {
      const time = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
      timeOptions.push(time);
    }
  }

  const tidsleveringType = surchargeTypes.find(s => s.name === 'Tidslevering');

  return (
    <div className="order-form-page">
      <div className="order-form-header">
        <button className="btn-cancel" onClick={() => navigate('/')}>
          × {t('cancel')}
        </button>
        <span className="company-name">{user?.organization_name}</span>
        <div className="template-select">
          {templates.length > 0 ? (
            <select
              className="template-dropdown"
              defaultValue=""
              onChange={(e) => {
                const tmpl = templates.find(t => t.id === e.target.value);
                if (tmpl) applyTemplate(tmpl);
              }}
            >
              <option value="" disabled>{t('template')}</option>
              {templates.map(tmpl => (
                <option key={tmpl.id} value={tmpl.id}>{tmpl.name}</option>
              ))}
            </select>
          ) : (
            <span>{t('template')}</span>
          )}
        </div>
      </div>

      <div className="order-form-container">
        <form onSubmit={handleSubmit} className="order-form">
          <h1 className="form-title">{editBookingId ? t('editBooking') : t('orderBooking')}</h1>
          {editBookingId && (
            <div className="edit-notice">
              {t('editNotice')}
            </div>
          )}

          {addressesLoading ? (
            <p className="loading-text">{t('loading')}</p>
          ) : addresses.length === 0 ? (
            <div className="info-message">
              Der er ingen adresser i adresseregisteret endnu. Kontakt Ole Larsen Transport for at få oprettet adresser.
            </div>
          ) : null}

          <div className="form-columns">
            {/* Afsender */}
            <section className="form-section">
              <h2 className="section-title">{t('sender')}</h2>

              <div className="form-group">
                <AddressSearch
                  addresses={addresses.filter(a => a.type === 'pickup' || a.type === 'both')}
                  value={pickupAddressId}
                  onChange={setPickupAddressId}
                  placeholder={t('selectAddress')}
                  favoriteKey="pickup"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Afhentningsdato</label>
                  <input
                    type="date"
                    value={pickupDate}
                    onChange={(e) => setPickupDate(e.target.value)}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Afhentes tidligst</label>
                  <select
                    value={pickupTime}
                    onChange={(e) => setPickupTime(e.target.value)}
                    className="form-select"
                  >
                    {timeOptions.map((time) => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <input
                  type="text"
                  placeholder="Bemærkning til afhentning"
                  value={pickupNotes}
                  onChange={(e) => setPickupNotes(e.target.value)}
                  className="form-input"
                />
              </div>
            </section>

            {/* Modtager */}
            <section className="form-section">
              <h2 className="section-title">{t('recipient')}</h2>

              <div className="form-group">
                <AddressSearch
                  addresses={addresses.filter(a => a.type === 'delivery' || a.type === 'both')}
                  value={deliveryAddressId}
                  onChange={setDeliveryAddressId}
                  placeholder={t('selectAddress')}
                  favoriteKey="delivery"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Leveringsdato</label>
                  <input
                    type="date"
                    value={deliveryDate}
                    onChange={(e) => setDeliveryDate(e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Leveres senest</label>
                  <select
                    value={deliveryTime}
                    onChange={(e) => setDeliveryTime(e.target.value)}
                    className="form-select"
                  >
                    <option value="">-</option>
                    {timeOptions.map((time) => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <input
                  type="text"
                  placeholder="Bemærkning til levering"
                  value={deliveryNotes}
                  onChange={(e) => setDeliveryNotes(e.target.value)}
                  className="form-input"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Faktura ref."
                    value={bookingReference}
                    onChange={(e) => setBookingReference(e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Leverandør ref."
                    value={supplierRef}
                    onChange={(e) => setSupplierRef(e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>
            </section>
          </div>

          {/* AI Suggestions */}
          {suggestions && suggestions.suggested_lines && suggestions.suggested_lines.length > 0 && (
            <div className="ai-suggestions">
              <div className="ai-suggestions-header">
                <span className="ai-suggestions-icon">AI</span>
                <span>Baseret på {suggestions.matching_bookings_count} tidligere bookinger</span>
              </div>
              <div className="ai-suggestions-chips">
                {suggestions.suggested_lines.slice(0, 3).map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    className="ai-suggestion-chip"
                    onClick={() => {
                      const defaultUnit = units.length > 0 ? units[0].name : 'Læs';
                      setLines([{
                        goods_description: s.goods_description,
                        quantity: s.quantity || 1,
                        unit: s.unit || defaultUnit,
                        po_number: '',
                        pallet_eu: s.pallet_eu || '',
                        pallet_half: s.pallet_half || '',
                        pallet_quarter: s.pallet_quarter || '',
                        return_pallet_eu: s.return_pallet_eu || '',
                        return_pallet_half: s.return_pallet_half || '',
                        return_pallet_quarter: s.return_pallet_quarter || '',
                        temperature_requirement: s.temperature_requirement || '',
                        time_delivery: false,
                      }]);
                      if (suggestions.suggested_pickup_time) {
                        setPickupTime(suggestions.suggested_pickup_time);
                      }
                    }}
                  >
                    {s.goods_description || 'Booking'} — {s.quantity} {s.unit}
                    {s.temperature_requirement ? ` (${s.temperature_requirement})` : ''}
                    <span className="chip-freq">×{s.frequency}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Gods & Varelinjer */}
          <section className="form-section">
            <div className="section-title-row">
              <h2 className="section-title" style={{ marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>Varelinjer</h2>
              <button type="button" className="btn-add-line" onClick={addLine}>+ Tilføj varelinje</button>
            </div>

            {lines.map((line, idx) => (
              <div key={idx} className="goods-line">
                {lines.length > 1 && (
                  <div className="goods-line-header">
                    <span className="goods-line-number">Linje {idx + 1}</span>
                    <button type="button" className="btn-remove-line" onClick={() => removeLine(idx)}>×</button>
                  </div>
                )}
                <div className="form-row">
                  <div className="form-group flex-2">
                    <input
                      type="text"
                      placeholder="Varebeskrivelse"
                      value={line.goods_description}
                      onChange={(e) => updateLine(idx, 'goods_description', e.target.value)}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <input
                      type="text"
                      placeholder="PO Nr."
                      value={line.po_number}
                      onChange={(e) => updateLine(idx, 'po_number', e.target.value)}
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Antal</label>
                    <input
                      type="number"
                      min="1"
                      value={line.quantity}
                      onChange={(e) => updateLine(idx, 'quantity', parseInt(e.target.value) || 1)}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Enhed</label>
                    {units.length > 0 ? (
                      <select
                        value={line.unit}
                        onChange={(e) => updateLine(idx, 'unit', e.target.value)}
                        className="form-select"
                      >
                        {units.map((u) => (
                          <option key={u.id} value={u.name}>{u.name}</option>
                        ))}
                      </select>
                    ) : (
                      <select
                        value={line.unit}
                        onChange={(e) => updateLine(idx, 'unit', e.target.value)}
                        className="form-select"
                      >
                        <option value="Læs">Læs</option>
                        <option value="Paller">Paller</option>
                        <option value="Kolli">Kolli</option>
                        <option value="Ton">Ton</option>
                      </select>
                    )}
                  </div>
                  <div className="form-group">
                    <label>{t('euPallets')}</label>
                    <input
                      type="number"
                      min="0"
                      placeholder="0"
                      value={line.pallet_eu}
                      onChange={(e) => updateLine(idx, 'pallet_eu', e.target.value)}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('halfPallets')}</label>
                    <input
                      type="number"
                      min="0"
                      placeholder="0"
                      value={line.pallet_half}
                      onChange={(e) => updateLine(idx, 'pallet_half', e.target.value)}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('quarterPallets')}</label>
                    <input
                      type="number"
                      min="0"
                      placeholder="0"
                      value={line.pallet_quarter}
                      onChange={(e) => updateLine(idx, 'pallet_quarter', e.target.value)}
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-row emballage-return-row">
                  <div className="form-group">
                    <label className="return-label">{t('returnEu')}</label>
                    <input
                      type="number"
                      min="0"
                      placeholder="0"
                      value={line.return_pallet_eu}
                      onChange={(e) => updateLine(idx, 'return_pallet_eu', e.target.value)}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="return-label">{t('returnHalf')}</label>
                    <input
                      type="number"
                      min="0"
                      placeholder="0"
                      value={line.return_pallet_half}
                      onChange={(e) => updateLine(idx, 'return_pallet_half', e.target.value)}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="return-label">{t('returnQuarter')}</label>
                    <input
                      type="number"
                      min="0"
                      placeholder="0"
                      value={line.return_pallet_quarter}
                      onChange={(e) => updateLine(idx, 'return_pallet_quarter', e.target.value)}
                      className="form-input"
                    />
                  </div>
                  <div className="emballage-hint">{t('returnHint')}</div>
                </div>

                {/* Temperatur & Tidslevering per linje */}
                <div className="line-extras">
                  {tempRequirements.length > 0 && (
                    <div className="form-group">
                      <label>Temperatur</label>
                      <div className="temp-chips">
                        <button
                          type="button"
                          className={`temp-chip ${line.temperature_requirement === '' ? 'selected' : ''}`}
                          onClick={() => updateLine(idx, 'temperature_requirement', '')}
                        >
                          Ingen krav
                        </button>
                        {tempRequirements.map((req) => (
                          <button
                            type="button"
                            key={req.id}
                            className={`temp-chip ${line.temperature_requirement === req.name ? 'selected' : ''}`}
                            onClick={() => updateLine(idx, 'temperature_requirement',
                              line.temperature_requirement === req.name ? '' : req.name
                            )}
                          >
                            {req.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {tidsleveringType && (
                    <div className="form-group">
                      <label
                        className="surcharge-checkbox"
                        onClick={() => updateLine(idx, 'time_delivery', !line.time_delivery)}
                      >
                        <span className={`checkbox-box ${line.time_delivery ? 'checked' : ''}`}>
                          {line.time_delivery ? '✓' : ''}
                        </span>
                        <span className="surcharge-label">
                          Tidslevering
                          <span className="surcharge-amount">
                            (+{tidsleveringType.default_amount} kr)
                          </span>
                        </span>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </section>

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <div className="form-actions">
            {showSaveTemplate ? (
              <div className="save-template-form">
                <input
                  type="text"
                  placeholder="Skabelonnavn..."
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  className="form-input template-name-input"
                  autoFocus
                />
                <button
                  type="button"
                  className="btn-save-template"
                  onClick={handleSaveTemplate}
                  disabled={savingTemplate || !templateName.trim()}
                >
                  {savingTemplate ? 'Gemmer...' : 'Gem'}
                </button>
                <button
                  type="button"
                  className="btn-cancel-template"
                  onClick={() => { setShowSaveTemplate(false); setTemplateName(''); }}
                >
                  Annuller
                </button>
              </div>
            ) : (
              <a
                href="#"
                className="save-template-link"
                onClick={(e) => { e.preventDefault(); setShowSaveTemplate(true); }}
              >
                {t('saveAsTemplate')}
              </a>
            )}
            <button type="submit" className="btn-send" disabled={loading || editLoading || addresses.length === 0}>
              {loading ? t('sending') : editBookingId ? t('saveChanges') : t('send')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default OrderForm;
